import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FilterToolbar from './FilterToolbar';
import { debounce } from 'lodash';

const API_URL = '/api/users';

const getDefaultFilters = () => ({
  q: '',
  startDate: '',
  endDate: '',
  statuses: [],
});

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = debounce(async (currentFilters, currentSortConfig) => {
      try {
        setLoading(true);
        const params = {
          q: currentFilters.q,
          sort_by: currentSortConfig.key,
          sort_direction: currentSortConfig.direction,
        };
        const response = await axios.get(API_URL, { params });
        setUsers(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users.');
      } finally {
        setLoading(false);
      }
    }, 500);

    fetchUsers(filters, sortConfig);

    return () => {
      fetchUsers.cancel();
    };
  }, [filters, sortConfig]);

  const handleEdit = (id) => {
    navigate(`/users/edit/${id}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/new')}
        >
          Create User
        </Button>
      </Box>

      <FilterToolbar
        filters={filters}
        onFilterChange={setFilters}
        statusOptions={[]}
      />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'username'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('username')}
                  >
                    Username
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'full_name'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('full_name')}
                  >
                    Full Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'role'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('role')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'is_active'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('is_active')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{highlightText(user.username, filters.q)}</TableCell>
                  <TableCell>{highlightText(user.full_name, filters.q)}</TableCell>
                  <TableCell>{highlightText(user.role, filters.q)}</TableCell>
                  <TableCell>{user.is_active ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(user.id)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default UserList;
